import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import { t } from '@/i18n';

/** Kleine, konsistente Bausteine im Stil des Mockups. Kein Framework — bewusst. */

/**
 * Klassen zusammensetzen — und **die spätere gewinnt**.
 *
 * Zwei Tailwind-Klassen derselben Eigenschaft (`bg-panel2 bg-blue/10`,
 * `w-full w-44`, `py-2 py-1`) entscheidet nicht die Reihenfolge im Attribut,
 * sondern die im Stylesheet. Das hat dieses Projekt dreimal erwischt: die
 * blaue Markierung blieb grau, Eingabefelder blieben in voller Breite, elf
 * „kompakte" Felder rendern in voller Größe. `tailwind-merge` löst genau das:
 * Es kennt die Gruppen und lässt von jeder nur die letzte stehen.
 */
export function cx(...parts: (string | false | null | undefined)[]): string {
  return twMerge(parts.filter(Boolean).join(' '));
}

type Variant = 'default' | 'primary' | 'ghost' | 'danger';

type Size = 'sm' | 'md' | 'lg';

/**
 * Klassen eines Knopfs — auch für Links, die wie ein Knopf aussehen sollen.
 * Vorher stand dafür ein `<Button>` in einem `<Link>`: zwei verschachtelte
 * Bedienelemente, zwei Tabstopps, und Vorleseprogramme lasen beides vor.
 */
export function buttonClass(variant: Variant = 'default', size: Size = 'md', className?: string): string {
  const base =
    'inline-flex items-center justify-center gap-2 font-bold tracking-tight border cursor-pointer select-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap no-underline [&>svg]:shrink-0';
  // Am Handy mindestens 44 px hoch — mit dem Daumen zu treffen; am Laptop bleibt es kompakt.
  // `sm` bleibt am Handy etwas niedriger (40 px): Es steht in dichten Tabellenzeilen
  // Am Handy darf die Beschriftung umbrechen: Zwei Knöpfe nebeneinander („Backup herunterladen", „Backup
  // wiederherstellen") waren bei 360 px breiter als der Schirm und schoben die ganze Seite seitlich
  const sizes = {
    sm: 'text-xs px-2.5 py-1.5 max-md:min-h-10',
    md: 'text-[13px] px-3 py-[7px] max-md:min-h-11 max-md:whitespace-normal max-md:text-left',
    lg: 'text-[15px] px-4 py-3 max-md:min-h-11 max-md:whitespace-normal',
  };
  const variants: Record<Variant, string> = {
    default: 'bg-raise border-line text-ink hover:bg-panel2',
    // Schrift auf der Akzentfarbe: hell oder dunkel, je nachdem, was sich besser abhebt (--color-on-red, index.css)
    primary: 'bg-red border-red text-on-red hover:brightness-110',
    ghost: 'bg-transparent border-transparent text-muted hover:text-ink hover:bg-panel2',
    danger: 'bg-transparent border-line text-err hover:bg-err/10',
  };
  return cx(base, sizes[size], variants[variant], className);
}

export function Button({ variant = 'default', className, size = 'md', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size }) {
  return <button className={buttonClass(variant, size, className)} {...props} />;
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx('bg-panel2 border border-field px-3 py-2 text-[15px] outline-none focus:border-blue w-full placeholder:text-dim', className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx('bg-panel2 border border-field px-3 py-2 text-[14px] outline-none focus:border-blue w-full placeholder:text-dim font-mono', className)} {...props} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    // `min-w-0`: In einem Raster darf der längste Eintrag die Spalte nicht breiter ziehen als die Seite (RuleEditor bei 800 px)
    <select className={cx('bg-panel2 border border-field px-3 py-2 text-[15px] outline-none focus:border-blue w-full min-w-0', className)} {...props}>
      {children}
    </select>
  );
}

/**
 * Beschriftung + Hinweis über einem Eingabefeld.
 *
 * Normalerweise ein `<label>`: Dann gehört die Beschriftung zum ersten
 * Bedienelement darin, und ein Klick darauf setzt den Fokus dorthin.
 *
 * Steht darin aber **nur ein Knopf** und kein Eingabefeld, greift dieselbe
 * Regel gegen uns: Ein `<button>` ist ebenfalls „beschriftbar", also zieht das
 * `<label>` seinen Namen an sich — der Knopf heißt dann für Vorleseprogramme
 * (und für Tests, die ihn über seine Beschriftung suchen) nicht mehr
 * „Leitungs-Link anzeigen", sondern der ganze Absatz samt Hinweistext. In dem
 * Fall `as="div"` setzen: gleiche Optik, aber ohne die Verknüpfung.
 */
export function Field({ label, hint, children, className, as = 'label' }: { label: string; hint?: string; children: ReactNode; className?: string; as?: 'label' | 'div' }) {
  const Tag = as;
  return (
    <Tag className={cx('block', className)}>
      <span className="label block mb-1.5">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-xs text-dim">{hint}</span>}
    </Tag>
  );
}

export function Pill({ children, tone = 'default', className }: { children: ReactNode; tone?: 'default' | 'live' | 'warn' | 'red' | 'blue'; className?: string }) {
  const tones = {
    default: 'border-line text-muted',
    live: 'border-live text-live',
    warn: 'border-warn text-warn',
    red: 'border-red text-red',
    blue: 'border-blue text-blue',
  };
  return <span className={cx('label inline-block border px-2 py-[3px] whitespace-nowrap', tones[tone], className)}>{children}</span>;
}

export function Card({ children, className, title, aside }: { children: ReactNode; className?: string; title?: ReactNode; aside?: ReactNode }) {
  return (
    <section className={cx('bg-panel2 border border-line', className)}>
      {(title || aside) && (
        <header className="flex items-center gap-3 px-4 py-2.5 border-b border-line2">
          <div className="font-mono text-xs tracking-[.1em] uppercase">{title}</div>
          <div className="ml-auto flex items-center gap-2">{aside}</div>
        </header>
      )}
      {children}
    </section>
  );
}

export function SectionHead({ title, meta, children }: { title: string; meta?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 mb-3 flex-wrap">
      <h2 className="text-[15px] font-bold tracking-tight m-0">{title}</h2>
      {meta && <p className="m-0 font-mono text-[11px] text-dim">{meta}</p>}
      {/* `min-w-0 max-w-full`: Am Handy darf die Knopfgruppe nicht breiter werden als die Seite — sie bricht um */}
      <div className="ml-auto flex items-center gap-2 flex-wrap justify-end min-w-0 max-w-full">{children}</div>
    </div>
  );
}

/**
 * Zuletzt fokussierte Elemente — damit ein Dialog den Fokus beim Schließen
 * zurückgeben kann, auch wenn `autoFocus` ihn schon vor dem Effekt in den
 * Dialog gezogen hat (dann ist `document.activeElement` im Effekt bereits das
 * Feld im Dialog, nicht mehr der Öffner-Knopf).
 */
const fokusVerlauf: HTMLElement[] = [];
if (typeof document !== 'undefined') {
  document.addEventListener(
    'focusin',
    (e) => {
      const el = e.target as HTMLElement | null;
      if (!el || el === document.body) return;
      fokusVerlauf.push(el);
      if (fokusVerlauf.length > 12) fokusVerlauf.shift();
    },
    true,
  );
}
function fokusVorDialog(dialog: HTMLElement | null): HTMLElement | null {
  const aktiv = document.activeElement as HTMLElement | null;
  if (!dialog || !aktiv || !dialog.contains(aktiv)) return aktiv;
  for (let i = fokusVerlauf.length - 1; i >= 0; i--) {
    const el = fokusVerlauf[i] as HTMLElement;
    if (el.isConnected && !dialog.contains(el)) return el;
  }
  return null;
}

const FOKUSSIERBAR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({
  open,
  onClose,
  title,
  children,
  width = 'max-w-2xl',
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: string;
  footer?: ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const titelId = useId();
  // `onClose` ist meist eine frisch erzeugte Funktion; ohne diesen Umweg liefe der
  // Effekt bei jedem Neurendern der Seite (Uhr, Cloud-Abgleich) noch einmal und
  // holte den Fokus aus dem Eingabefeld zurück — Tippen war dann unmöglich.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    // Fokus einmal beim Öffnen in den Dialog, damit Tastendrücke nicht den Öffner-Button dahinter treffen —
    // und beim Schließen zurück auf das Element, von dem aus geöffnet wurde
    const el = root.current;
    const vorher = fokusVorDialog(el);
    // `autoFocus` (React setzt den Fokus schon beim Einhängen) hat Vorrang — vorher
    // überschrieb `el.focus()` ihn, und Enter traf nicht den gemeinten Knopf.
    // Sonst ein ausdrücklich markiertes Element, sonst das erste bedienbare.
    if (el && !(document.activeElement && el.contains(document.activeElement))) {
      const ziel = el.querySelector<HTMLElement>('[autofocus], [data-autofocus]') ?? [...el.querySelectorAll<HTMLElement>(FOKUSSIERBAR)].find((x) => x.offsetParent !== null);
      (ziel ?? el).focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Liegt eine Rückfrage (DialogHost) über diesem Dialog, schließt Escape nur sie — nicht beides
        const oben = document.querySelector('[data-dialog="ok"]');
        if (oben && !el?.contains(oben)) return;
        return closeRef.current();
      }
      // Tab bleibt im Dialog: Vorher tabbte man hinter das Overlay in die Seite
      if (e.key !== 'Tab' || !el) return;
      const fokussierbar = [...el.querySelectorAll<HTMLElement>(FOKUSSIERBAR)].filter((x) => x.offsetParent !== null);
      if (fokussierbar.length === 0) return;
      const erstes = fokussierbar[0] as HTMLElement;
      const letztes = fokussierbar[fokussierbar.length - 1] as HTMLElement;
      if (e.shiftKey && (document.activeElement === erstes || document.activeElement === el)) {
        e.preventDefault();
        letztes.focus();
      } else if (!e.shiftKey && document.activeElement === letztes) {
        e.preventDefault();
        erstes.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      vorher?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-start justify-center bg-black/60 p-0 md:p-4 overflow-y-auto" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={root}
        tabIndex={-1}
        className={cx('bg-panel border border-line w-full mt-0 md:mt-[6vh] shadow-[0_30px_80px_-40px_#000] flex flex-col max-h-[94vh] md:max-h-[88vh] outline-none', width)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
      >
        <header className="flex items-center gap-3 px-4 md:px-5 py-3 border-b border-line">
          <h2 id={titelId} className="m-0 text-[15px] font-bold tracking-tight">
            {title}
          </h2>
          {/* Am Handy 44 × 44 px — mit dem Daumen zu treffen, ohne die Kopfzeile höher zu machen */}
          <button
            type="button"
            className="ml-auto text-dim hover:text-ink cursor-pointer p-1 -m-1 max-md:-my-2.5 max-md:-mr-2.5 max-md:min-h-11 max-md:min-w-11 inline-flex items-center justify-center"
            onClick={onClose}
            aria-label={t('Schließen')}
          >
            <X size={20} />
          </button>
        </header>
        <div className="p-4 md:p-5 overflow-y-auto min-h-0">{children}</div>
        {footer && <footer className="flex items-center gap-2 px-4 md:px-5 py-3 border-t border-line flex-wrap">{footer}</footer>}
      </div>
    </div>
  );
}

/**
 * Rückmeldung an Ort und Stelle: grün (Erfolg, `role="status"`) oder rot
 * (Fehler, `role="alert"` — Vorleseprogramme sagen ihn sofort an), mit
 * eigenem Schließen-Knopf statt „irgendwo draufklicken". `detail` ist der
 * technische Wortlaut (etwa des Anmeldedienstes) — aufklappbar, damit er
 * nicht den Satz für Menschen verdrängt, aber für den Support da ist.
 */
export function Meldung({ ton, children, detail, onClose, className }: { ton: 'ok' | 'hinweis' | 'fehler'; children: ReactNode; detail?: string; onClose?: () => void; className?: string }) {
  const farbe = { ok: 'border-live/60 bg-live/10 text-live', hinweis: 'border-warn/60 bg-warn/10 text-warn', fehler: 'border-err/60 bg-err/10 text-err' }[ton];
  return (
    <div role={ton === 'fehler' ? 'alert' : 'status'} data-meldung={ton} className={cx('flex items-start gap-2 border px-3 py-2 text-sm leading-relaxed', farbe, className)}>
      <div className="flex-1 min-w-0">
        <p className="m-0">{children}</p>
        {detail && (
          <details className="mt-1 text-xs text-muted">
            <summary className="cursor-pointer">{t('Technische Details')}</summary>
            <code className="block mt-1 font-mono break-words whitespace-pre-wrap">{detail}</code>
          </details>
        )}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={t('Meldung schließen')}
          className="shrink-0 cursor-pointer text-dim hover:text-ink p-1 -m-1 max-md:min-h-11 max-md:min-w-11 max-md:-my-3 max-md:-mr-2 inline-flex items-center justify-center"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="border border-dashed border-line p-8 text-center">
      <div className="font-bold mb-1">{title}</div>
      <div className="text-sm text-muted">{children}</div>
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="font-mono text-[10px] border border-line px-1 py-0.5 text-dim">{children}</kbd>;
}
